import { MemberProductEligibilityDialogComponent } from '../../../modal/member-product-eligibility-dialog/member-product-eligibility-dialog.component';
import {Component, OnDestroy, OnInit, ViewChild, ElementRef} from '@angular/core';
import {MatMenuTrigger} from '@angular/material/menu';
import {MatTableDataSource} from '@angular/material/table';
import {MatSnackBar} from '@angular/material/snack-bar';
import {MatDialog} from '@angular/material/dialog';
import {UsersService} from '../../../../services/users/users.service';
import {UiAlertService} from '../../../../services/ui-alert/ui-alert.service';
import {Router} from '@angular/router';
import {AppSettings} from '../../../../app.settings';
import {PlaidSandboxToolDialogComponent} from '../../../customersupport/manageusers/plaid-sandbox-tool-dialog.component';
import { Subject, Subscription } from 'rxjs';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { AssociateCompanyRoleDialogComponent } from '../associate-company-role-dialog.component';
import {
  SuperadminUsersStripePaymentsDialogComponent,
} from '../details/superadmin-users-stripe-payments-dialog.component';
import { UserTeamMembershipsDialogComponent } from '../user-team-memberships-dialog.component';
import { ManageScopedAccessDialogComponent } from '../manage-scoped-access-dialog.component';
import { exactScopedTarget } from '../../../../services/scoped-access/scoped-access.models';
import { SessionService } from '../../../../services/session/session.service';

interface UserBrandHistoryItem {
  brandId: string;
  joinedAt: string;
  leftAt: string | null;
  brandName: string;
  brandLogoUrl: string;
}

@Component({
  selector: 'app-superadmin-users-detail',
  templateUrl: './superadmin-users-detail.component.html',
  styleUrls: ['./superadmin-users-detail.component.scss'],
})
export class SuperadminUsersDetailComponent implements OnInit, OnDestroy {
  private static readonly EXACT_SUPERADMIN_PERMISSION = 2147483647;

  @ViewChild('searchInput') searchInput: ElementRef | undefined;

  public loading = false;
  public items = new MatTableDataSource<any>([]);
  public columnsToDisplay: string[] = ['select', 'name', 'brand', 'phone', 'email', 'profile', 'createdAt', 'actions'];

  public search = '';
  public isAdmin = false;
  public isDemo = false;
  public selectedBrand = '';
  public brandList: any[] = [];
  public removeList: {userId: string}[] = [];
  public showDeletedList = false;
  public plaidSandboxToolsEnabled = AppSettings.isQa || AppSettings.isLocal || AppSettings.apiParam === 'local';

  private page = 0;
  private limit = 30;
  private endOfList = false;
  private readonly searchDebounceMs = 300;
  private readonly filterValueChanges = new Subject<string>();
  private readonly filterSubscription: Subscription;

  constructor(
    private usersService: UsersService,
    private sessionService: SessionService,
    private uiAlertService: UiAlertService,
    private snackBar: MatSnackBar,
    private router: Router,
    private dialog: MatDialog,
  ) {
    this.filterSubscription = this.filterValueChanges
      .pipe(
        debounceTime(this.searchDebounceMs),
        distinctUntilChanged(),
      )
      .subscribe(() => this.loadUsers(true));
  }

  ngOnInit(): void {
    this.loadBrands();
    this.loadUsers(true);
  }

  ngOnDestroy(): void {
    this.filterSubscription.unsubscribe();
  }

  async loadBrands(): Promise<void> {
    try {
      this.brandList = await this.usersService.getBrands();
      this.refreshUserBrandInfo();
    } catch (error) {
      console.error('Error loading brands', error);
    }
  }

  async loadUsers(clear = false): Promise<void> {
    if (clear) {
      this.page = 0;
      this.endOfList = false;
      this.items.data = [];
    }
    if (this.loading || this.endOfList) {
      return;
    }
    this.loading = true;
    try {
      const results = await this.usersService.searchAccounts(
        this.search,
        this.isAdmin,
        this.isDemo,
        this.selectedBrand,
        this.page,
        this.limit,
      );
      if (!results || results.length === 0) {
        this.endOfList = true;
      } else {
        this.items.data = this.items.data.concat(results.map((user: any) => this.decorateUser(user)));
        this.page += 1;
      }
    } catch (error) {
      console.error('Error loading users', error);
    } finally {
      this.loading = false;
    }
  }

  onScroll(event: any): void {
    const scrollTop = event.target.scrollTop;
    const scrollHeight = event.target.scrollHeight;
    const offsetHeight = event.target.offsetHeight;
    const threshold = 150;
    if (scrollTop + offsetHeight >= scrollHeight - threshold) {
      this.loadUsers();
    }
  }

  queueFilters(): void {
    const filterState = JSON.stringify({
      search: this.search,
      isAdmin: this.isAdmin,
      isDemo: this.isDemo,
      selectedBrand: this.selectedBrand,
    });
    this.filterValueChanges.next(filterState);
  }

  clearFilters(): void {
    this.search = '';
    this.isAdmin = false;
    this.isDemo = false;
    this.selectedBrand = '';
    this.loadUsers(true);
  }

  getBrandName(user: any): string {
    return `${user.brandName || user.brand?.name || user.partnerName || user.partnerId || '-'}`.trim() || '-';
  }

  onBrandLogoError(user: any): void {
    user.brandLogoUrl = '';
  }

  getBrandHistory(user: any): UserBrandHistoryItem[] {
    return user.brandHistoryItems || [];
  }

  onBrandHistoryLogoError(historyItem: UserBrandHistoryItem): void {
    historyItem.brandLogoUrl = '';
  }

  isPhoneVerified(user: any): boolean {
    return this.isVerifiedFlag(user?.isConfirmed);
  }

  isEmailVerified(user: any): boolean {
    return typeof user?.email === 'string' && user.email.trim().length > 0
      && this.isVerifiedFlag(user.isVerifiedEmail);
  }

  toggleRemove(user: any, checked: boolean): void {
    const userId = `${user?.id || ''}`.trim();
    if (!userId) {
      return;
    }
    if (checked) {
      if (!this.isMarkedForRemoval(user)) {
        this.removeList.push({ userId });
      }
    } else {
      this.removeList = this.removeList.filter((item) => item.userId !== userId);
    }
  }

  isMarkedForRemoval(user: any): boolean {
    const userId = `${user?.id || ''}`.trim();
    return !!userId && this.removeList.some((item) => item.userId === userId);
  }

  stopRowClick(event: Event): void {
    event.stopPropagation();
  }

  async removeUsers(): Promise<void> {
    if (this.showDeletedList || this.removeList.length === 0) {
      return;
    }
    this.showDeletedList = true;
    try {
      const confirm = await this.uiAlertService.presentAlertConfirm('Selected users will be deleted. Continue?');
      if (!confirm) {
        return;
      }
      const selectedUsers = [...this.removeList];
      const failedUsers: {userId: string}[] = [];
      for (const user of selectedUsers) {
        try {
          await this.usersService.deleteUserById(user.userId);
        } catch (error) {
          failedUsers.push(user);
          console.error('Error deleting user', error);
        }
      }
      this.removeList = failedUsers;
      if (failedUsers.length > 0) {
        this.snackBar.open(
          `${failedUsers.length} of ${selectedUsers.length} user deletions failed. Failed users remain selected.`,
          'Close',
        );
      }
      await this.loadUsers(true);
    } finally {
      this.showDeletedList = false;
    }
  }

  userEdit(user: any): void {
    this.uiAlertService.presentAddUser(user).then((result) => {
      if (result) {
        Object.assign(user, result);
      }
      this.loadUsers(true);
    });
  }

  openUser(user: any): void {
    // placeholder: keep parity with admin-fe navigation for user profile
  }

  loginUser(user: any, devMode: boolean): void {
    this.usersService.loginUserV3(user.id, devMode, (url) => {
      window.open(url, '_blank');
    });
  }

  loginTeamAdmin(user: any, devMode: boolean): void {
    this.usersService.loginTeamAdmin(user.id, devMode, (url) => {
      window.open(url, '_blank');
    });
  }

  sendMessage(user: any): void {
    this.router.navigate(['/superadmin/users/push'], { queryParams: { userId: user.id } }).then();
  }

  canDemoteExactSuperadmin(user: any): boolean {
    return this.sessionService.isExactSuperAdmin()
      && user?.permissionLevel === SuperadminUsersDetailComponent.EXACT_SUPERADMIN_PERMISSION
      && this.isDifferentUser(user);
  }

  showExactSuperadminApprovalNotice(user: any): boolean {
    return this.sessionService.isExactSuperAdmin()
      && user?.permissionLevel !== SuperadminUsersDetailComponent.EXACT_SUPERADMIN_PERMISSION
      && this.isDifferentUser(user);
  }

  private isDifferentUser(user: any): boolean {
    const targetUserId = `${user?.id || ''}`.trim();
    const actorUserId = `${this.sessionService.getUserId() || ''}`.trim();
    return !!targetUserId && !!actorUserId && targetUserId !== actorUserId;
  }

  async demoteExactSuperadmin(user: any): Promise<void> {
    if (!this.canDemoteExactSuperadmin(user)) {
      return;
    }
    try {
      await this.usersService.demoteExactSuperadmin(user.id);
      this.snackBar.open('Exact superadmin access removed', 'Close', { duration: 2000 });
      await this.loadUsers(true);
    } catch (error: any) {
      console.error('Error updating admin permission', error);
      this.snackBar.open(
        error?.data?.error || 'Exact superadmin access could not be changed',
        'Close',
        { duration: 3000 },
      );
    }
  }

  async toggleDemo(user: any): Promise<void> {
    try {
      await this.usersService.setDemoMode(user.id, !user.isDemo);
      this.snackBar.open(`User demo mode updated`, 'Close', { duration: 2000 });
      this.loadUsers(true);
    } catch (error) {
      console.error('Error updating demo mode', error);
    }
  }

  adjustDwollaBalance(user: any): void {
    this.router.navigate(['/superadmin/users/dwolla', user.id]).then();
  }

  openStripePayments(user: any): void {
    this.dialog.open(SuperadminUsersStripePaymentsDialogComponent, {
      width: '1100px',
      maxWidth: '98vw',
      data: { user },
    });
  }

  openPlaidSandboxTools(user: any): void {
    if (!this.plaidSandboxToolsEnabled) {
      this.snackBar.open('Plaid Sandbox Tools are available in local and QA only', 'Close', {duration: 3000});
      return;
    }
    this.dialog.open(PlaidSandboxToolDialogComponent, {
      width: '980px',
      maxWidth: '96vw',
      data: {user}
    });
  }

  canManageScopedAccess(user: any): boolean {
    return exactScopedTarget(this.sessionService, user?.id);
  }

  manageScopedAccess(user: any): void {
    if (!this.canManageScopedAccess(user)) { return; }
    this.dialog.open(ManageScopedAccessDialogComponent, {
      width: '820px', maxWidth: '96vw', data: {user},
    });
  }

  associateCompanyRole(user: any): void {
    this.dialog.open(AssociateCompanyRoleDialogComponent, {
      width: '760px',
      maxWidth: '96vw',
      data: {user},
    });
  }

  canSetProductEligibility(): boolean {
    return this.sessionService.isExactSuperAdmin();
  }

  setProductEligibility(user: any): void {
    if (!this.canSetProductEligibility()) { return; }
    this.dialog.open(MemberProductEligibilityDialogComponent, {
      width: '520px', maxWidth: '96vw', data: {user},
    }).afterClosed().subscribe(result => {
      if (result) {
        const count = result.productIds.length;
        this.snackBar.open(`Product eligibility saved for ${count} product${count === 1 ? '' : 's'}`, 'Close', {duration: 4000});
      }
    });
  }

  openTeamMemberships(user: any): void {
    this.dialog.open(UserTeamMembershipsDialogComponent, {
      width: '760px',
      maxWidth: '96vw',
      data: {user},
    });
  }

  private decorateUser(user: any): any {
    const brand = this.findBrandForUser(user);
    return {
      ...user,
      brandName: user.brand?.name || user.partnerName || brand?.name || user.partnerId || '',
      brandLogoUrl: user.brand?.logoUrl
        || brand?.logoUrl
        || brand?.horizontalLogoOnWhite
        || brand?.horizontalLogoUrl
        || brand?.lightLogoUrl
        || '',
      brandHistoryItems: this.decorateBrandHistory(user),
    };
  }

  private decorateBrandHistory(user: any): UserBrandHistoryItem[] {
    if (!Array.isArray(user?.metadata?.brandHistory)) {
      return [];
    }

    return user.metadata.brandHistory.map((historyItem: any) => {
      const brandId = `${historyItem?.brandId || ''}`.trim();
      const brand = this.brandList.find((item) => `${item?.partnerId || ''}`.trim() === brandId);
      return {
        brandId,
        joinedAt: historyItem?.joinedAt,
        leftAt: historyItem?.leftAt,
        brandName: brand?.name || '',
        brandLogoUrl: brand?.logoUrl || '',
      };
    });
  }

  private findBrandForUser(user: any): any {
    const partnerId = `${user?.partnerId || user?.brand?.partnerId || ''}`.trim();
    if (!partnerId) {
      return undefined;
    }
    return this.brandList.find((brand) => `${brand?.partnerId || ''}`.trim() === partnerId);
  }

  private refreshUserBrandInfo(): void {
    this.items.data = this.items.data.map((user: any) => this.decorateUser(user));
  }

  private isVerifiedFlag(value: any): boolean {
    return value === true || value === 1 || value === '1' || value === 'true';
  }
}
